import { useState } from "react";
import { Outlet } from "react-router-dom";
import { useT } from "@/lib/translation";
import { SlidersHorizontal } from "lucide-react";
import { ResearchNavigation } from "./ResearchNavigation";
import { ThemeToggle } from "./theme-toggle";
import { UserLanguageMenu } from "@/components/UserLanguageMenu";
import { PrivacyToggle } from "./PrivacyToggle";
import SocialLinks from "@/components/site/SocialLinks";
import ResearchSearchInput from "@/features/research/components/ResearchSearchInput";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import PageErrorFallback from "@/components/shared/PageErrorFallback";
import PageContentSuspense from "@/components/shared/PageContentSuspense";
import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import BillingGate from "@/components/billing/BillingGate";
import { UpgradeButton } from "@/components/billing/UpgradeButton";
import AnalysisWelcomeModal from "@/components/AnalysisWelcomeModal";

// TECH-DEBT: duplicated in Layout — mobile settings Sheet, header controls
// (PrivacyToggle/ThemeToggle/NotificationBell), ErrorBoundary/PageErrorFallback
// pattern, and footer text are identical. See Layout.tsx for the full duplication note.
const ResearchLayout = () => {
  const t = useT();
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div className="relative flex min-h-screen">
      <AnalysisWelcomeModal />
      <ResearchNavigation />
      <main className="flex-1 min-w-0 sm:ml-60 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 flex items-center justify-between h-14 border-b bg-background px-14 sm:px-6">
          <div className="flex min-w-0 items-center gap-4">
            <ResearchSearchInput
              size="sm"
              className="w-60 sm:w-72"
              placeholder={t("nav:research.searchPlaceholder")}
            />
          </div>

          <div className="flex items-center justify-end gap-4">
            <div className="hidden sm:flex items-center gap-4">
              <UpgradeButton />
              <UserLanguageMenu />
              <PrivacyToggle />
              <ThemeToggle />
              <NotificationBell />
            </div>
          </div>
        </header>
        {/* Mobile: fixed settings + bell buttons at right edge */}
        <div className="fixed top-3 right-14 z-40 sm:hidden">
          <NotificationBell />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="fixed top-3 right-4 z-40 h-8 w-8 sm:hidden"
          onClick={() => setSettingsOpen(true)}
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>

        <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
          <SheetContent side="right" className="w-64">
            <SheetHeader>
              <SheetTitle>{t("nav:layout.display")}</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col gap-6 pt-6">
              <UpgradeButton className="w-full" />
              <UserLanguageMenu withLabel />
              <div>
                <p className="text-sm font-medium mb-3 text-muted-foreground">
                  {t("nav:layout.privacy")}
                </p>
                <PrivacyToggle />
              </div>
              <div>
                <p className="text-sm font-medium mb-3 text-muted-foreground">
                  {t("nav:layout.theme")}
                </p>
                <ThemeToggle />
              </div>
            </div>
          </SheetContent>
        </Sheet>
        <div className="p-6 bg-muted/50 flex-1 overflow-x-clip">
          <ErrorBoundary
            fallbackRender={({ resetError }) => (
              <PageErrorFallback onReset={resetError} />
            )}
          >
            <BillingGate>
              <PageContentSuspense>
                <Outlet />
              </PageContentSuspense>
            </BillingGate>
          </ErrorBoundary>
        </div>
        <footer className="px-6 py-3 border-t grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="text-xs text-muted-foreground">
            <SocialLinks withLabel={false} />
          </div>
          <p className="text-xs text-muted-foreground text-center whitespace-nowrap">
            {t("nav:layout.disclaimer")}
          </p>
          <div />
        </footer>
      </main>
    </div>
  );
};

export default ResearchLayout;
